import { A as Autoplay, E as EmblaCarousel } from "./embla-CBF9-Aj4.js";
import { d as debounce, i as isMobile, o as onFirstInteractionIdle, l as lazyInit, f as duplicateSlidesForLoop, a as addPrevNextBtnsClickHandlersSSR, b as addDotsBtnsAndClickHandlersSSR, c as addInstagramDotBtnsAndClickHandlers } from "./utilities-Da0sjanJ.js";
class CollectionList extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.originalSlideCount = 0;
    this.cleanupFunctions = [];
    this.currentDeviceType = null;
    this._isFirstInit = true;
    this.nodes = {
      embla: null,
      viewport: null,
      container: null,
      prevBtn: null,
      nextBtn: null,
      dots: null
    };
    this.config = {
      showNavigation: false,
      showDotsDesktop: false,
      showDotsMobile: false,
      options: {},
      autoPlay: {
        desktop: false,
        mobile: false
      }
    };
    this.gridConfig = {
      desktop: { rows: 1, itemsPerRow: 4 },
      mobile: { rows: 1, itemsPerRow: 1 }
    };
    this.handleResize = debounce(() => this.onResize(), 300);
  }
  connectedCallback() {
    this.cacheNodes();
    this.ssrRows = Number(this.dataset.ssrRows) || 1;
    this._domRows = this.ssrRows;
    this.gridConfig = {
      desktop: {
        rows: Number(this.dataset.rowsDesktop) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowDesktop) || 4
      },
      mobile: {
        rows: Number(this.dataset.rowsMobile) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowMobile) || 1
      }
    };
    const container = this.nodes.container;
    this.originalItems = Array.from(
      container ? container.querySelectorAll(":scope > .embla__slide > .collection-list-item") : []
    );
    this.parseConfig();
    this.currentDeviceType = isMobile() ? "mobile" : "desktop";
    if (Shopify.designMode) {
      this.init();
    } else {
      onFirstInteractionIdle(() => {
        if (this.isConnected) lazyInit(this, () => this.init());
      });
    }
    window.addEventListener("resize", this.handleResize);
  }
  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    this.nodes.embla = this.querySelector(".embla");
    if (!this.nodes.embla) return;
    this.nodes.viewport = this.nodes.embla.querySelector(".embla__viewport");
    this.nodes.container = this.nodes.embla.querySelector(".embla__container");
    this.nodes.prevBtn = this.nodes.embla.querySelector(".embla__snip-button--prev button");
    this.nodes.nextBtn = this.nodes.embla.querySelector(".embla__snip-button--next button");
    this.nodes.dots = this.nodes.embla.querySelector(".embla__snip-dots");
  }
  parseConfig() {
    this.config.showNavigation = this.dataset.showNavigation === "true";
    this.config.showDotsDesktop = this.dataset.showDotsDesktop === "true";
    this.config.showDotsMobile = this.dataset.showDotsMobile === "true";
    this.config.options = this.parseJSON(this.dataset.options, {});
    const autoPlayDesktop = this.parseJSON(this.dataset.autoPlayDesktop, false);
    const autoPlayMobile = this.parseJSON(this.dataset.autoPlayMobile, false);
    this.config.autoPlay.desktop = autoPlayDesktop || false;
    this.config.autoPlay.mobile = autoPlayMobile || autoPlayDesktop;
  }
  parseJSON(jsonString, fallback) {
    if (!jsonString || !jsonString.trim()) return fallback;
    try {
      const parsed = JSON.parse(jsonString.replace(/,\s*([}\]])/g, "$1"));
      if (typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
      return fallback;
    } catch (error) {
      return fallback;
    }
  }
  getCurrentAutoPlayConfig() {
    return isMobile() ? this.config.autoPlay.mobile : this.config.autoPlay.desktop;
  }
  getCurrentGridConfig() {
    return isMobile() ? this.gridConfig.mobile : this.gridConfig.desktop;
  }
  // ========== Initialization ==========
  init() {
    this.destroy();
    const currentRows = this.getCurrentGridConfig().rows;
    if (this._domRows !== currentRows) {
      currentRows > 1 ? this.applyGridLayout() : this.flattenLayout();
      this._domRows = currentRows;
    }
    this.initEmbla();
  }
  initEmbla() {
    if (!this.nodes.viewport) return;
    const autoPlayConfig = this.getCurrentAutoPlayConfig();
    const plugins = [];
    if (autoPlayConfig) {
      plugins.push(
        Autoplay({
          delay: autoPlayConfig.delay ?? 4e3,
          stopOnInteraction: autoPlayConfig.stopOnInteraction ?? false,
          stopOnMouseEnter: autoPlayConfig.stopOnMouseEnter ?? true,
          stopOnFocusIn: false
        })
      );
    }
    try {
      this.emblaApi = EmblaCarousel(this.nodes.viewport, this.config.options, plugins);
      this.originalSlideCount = this.nodes.container.children.length;
      if (this.emblaApi.internalEngine().options.loop) {
        this.originalSlideCount = duplicateSlidesForLoop(this.emblaApi, this.nodes.container);
      }
      this.setupEmblaFeatures();
      this._isFirstInit = false;
    } catch (error) {
    }
  }
  setupEmblaFeatures() {
    const isActive = this.emblaApi.internalEngine().options.active;
    const snapCount = this.emblaApi.scrollSnapList().length;
    if (this.nodes.dots) this.nodes.dots.style.display = "";
    if (this.nodes.prevBtn) this.nodes.prevBtn.style.display = "";
    if (this.nodes.nextBtn) this.nodes.nextBtn.style.display = "";
    if (!isActive || snapCount <= 1) {
      if (this.nodes.dots) this.nodes.dots.style.display = "none";
      if (this.nodes.prevBtn) this.nodes.prevBtn.style.display = "none";
      if (this.nodes.nextBtn) this.nodes.nextBtn.style.display = "none";
      return;
    }
    if (this.config.showNavigation) this.enableNavigation();
    const isDesktop = !isMobile();
    const showDots = isDesktop ? this.config.showDotsDesktop : this.config.showDotsMobile;
    if (showDots) this.enableDots();
  }
  enableNavigation() {
    const { prevBtn, nextBtn } = this.nodes;
    if (!prevBtn || !nextBtn || !this.emblaApi) return;
    const cleanup = addPrevNextBtnsClickHandlersSSR(this.emblaApi, prevBtn, nextBtn);
    this.registerCleanup(cleanup);
  }
  enableDots() {
    const { dots } = this.nodes;
    if (!dots || !this.emblaApi) return;
    const dotsInner = dots.querySelector(".embla__dots-inner") || dots;
    const ssrDotCount = dotsInner.querySelectorAll(".embla__dot").length;
    const snapCount = this.emblaApi.scrollSnapList().length;
    let cleanup;
    if (this._isFirstInit && ssrDotCount === snapCount) {
      cleanup = addDotsBtnsAndClickHandlersSSR(this.emblaApi, dots);
    } else {
      cleanup = addInstagramDotBtnsAndClickHandlers(this.emblaApi, dotsInner);
    }
    this.registerCleanup(cleanup);
  }
  // ========== Public API (Theme Editor) ==========
  scrollToSlide(slideIndex) {
    var _a;
    (_a = this.emblaApi) == null ? void 0 : _a.scrollTo(slideIndex);
  }
  // ========== Grid Layout (mobile only — desktop is SSR) ==========
  flattenLayout() {
    const container = this.nodes.container;
    if (!container) return;
    const slides = this.originalItems.map((item) => {
      const slide = document.createElement("div");
      slide.className = "embla__slide";
      slide.appendChild(item);
      return slide;
    });
    container.replaceChildren(...slides);
  }
  applyGridLayout() {
    const { rows, itemsPerRow } = this.getCurrentGridConfig();
    const itemsPerPage = rows * itemsPerRow;
    const items = this.originalItems;
    const slides = [];
    for (let pageStart = 0; pageStart < items.length; pageStart += itemsPerPage) {
      const page = items.slice(pageStart, pageStart + itemsPerPage);
      const isPartial = page.length < itemsPerPage;
      if (isPartial) {
        for (let i = 0; i < page.length; i += rows) {
          const slide = document.createElement("div");
          slide.className = "embla__slide embla__slide-group";
          slide.style.setProperty("--slide-rows", rows);
          for (let r = 0; r < rows && i + r < page.length; r++) {
            slide.appendChild(page[i + r]);
          }
          slides.push(slide);
        }
      } else {
        for (let col = 0; col < itemsPerRow; col++) {
          const slide = document.createElement("div");
          slide.className = "embla__slide embla__slide-group";
          slide.style.setProperty("--slide-rows", rows);
          for (let row = 0; row < rows; row++) {
            const item = page[row * itemsPerRow + col];
            if (item) slide.appendChild(item);
          }
          slides.push(slide);
        }
      }
    }
    const container = this.nodes.container;
    if (container) container.replaceChildren(...slides);
  }
  // ========== Resize ==========
  onResize() {
    const newDevice = isMobile() ? "mobile" : "desktop";
    if (this.currentDeviceType !== newDevice) {
      this.currentDeviceType = newDevice;
      this.init();
    }
  }
  // ========== Cleanup ==========
  registerCleanup(fn) {
    if (fn && typeof fn === "function") this.cleanupFunctions.push(fn);
  }
  destroy() {
    this.cleanupFunctions.forEach((fn) => {
      try {
        fn();
      } catch {
      }
    });
    this.cleanupFunctions = [];
    if (this.emblaApi) {
      try {
        this.emblaApi.destroy();
      } catch {
      }
      this.emblaApi = null;
    }
    if (this.originalSlideCount > 0 && this.nodes.container) {
      const slides = Array.from(this.nodes.container.children);
      slides.slice(this.originalSlideCount).forEach((el) => el.remove());
    }
    this.originalSlideCount = 0;
  }
}
if (!customElements.get("collection-list-component")) {
  customElements.define("collection-list-component", CollectionList);
}
